import { Fragment, useMemo, useCallback, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { RagImageItem, RagVideoItem } from "../../types/ragChatTypes";
import {
  buildStepImagesByIndex,
  parseProcedureCompactDocument,
  procedureBlocksForDisplay,
  splitProcedureBlocksForDisplay,
  splitStepTitleBody,
  extractStepVideoReferences,
  extractStepActionLink,
  cleanStepDisplayText,
  isBareRemovingOrInstallingTitle,
  isBareNoteSectionTitle,
  type ProcedureBlock,
  type ProcedureBlockItem,
  type ParsedStep,
  type StepImageBinding,
} from "../../utils/ragChatUtils";
import {
  ANSWER_COUNT_CHIP_SX,
  ANSWER_HEADING_SX,
  ANSWER_TITLE_SX,
} from "../../constants/ragUiConstants";
import { ProcedureCompactSection } from "./ProcedureCompactSection";
import { StepCard } from "./StepCard";
import { RagMarkdownContent } from "./RagMarkdownContent";
import { RagImageLightbox, type RagLightboxItem } from "./RagImageLightbox";

export interface CatalogPostProcedureContentProps {
  /** Post-catalog Markdown/prose from Parts Catalog sectioning. */
  text: string;
  inlineImages?: Record<string, RagImageItem[]>;
  videos?: RagVideoItem[];
  showRichMedia?: boolean;
}

function stepImagesToGallery(
  bindings: StepImageBinding[],
  pageLabel: string
): RagLightboxItem[] {
  return bindings
    .filter(({ img }) => Boolean(img.path))
    .map(({ img, page }) => ({
      src: img.path,
      label: `${pageLabel}${page}`,
    }));
}

function ProcedureSectionHeader({ title }: { title: string }) {
  return (
    <Typography
      component="h3"
      data-testid="catalog-post-section-heading"
      sx={{ ...ANSWER_HEADING_SX, mb: 1 }}
    >
      {title}
    </Typography>
  );
}

/**
 * Renders Parts Catalog post-catalog content through the existing procedure
 * pipeline (compact cards + Step Card carousels). Falls back to Markdown when
 * no WI sections are detected.
 */
export function CatalogPostProcedureContent({
  text,
  inlineImages = {},
  videos = [],
  showRichMedia = true,
}: CatalogPostProcedureContentProps) {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState<{
    src: string;
    gallery?: RagLightboxItem[];
    title?: string;
  } | null>(null);
  const [scrollProgress, setScrollProgress] = useState<Record<string, number>>(
    {}
  );

  const doc = useMemo(() => parseProcedureCompactDocument(text), [text]);
  const displayBlocks = useMemo(
    () => (doc ? procedureBlocksForDisplay(doc.blocks) : []),
    [doc]
  );
  const { toolsBlocks, bodyBlocks } = useMemo(
    () => splitProcedureBlocksForDisplay(displayBlocks),
    [displayBlocks]
  );

  const wiStepImagesByBlock = useMemo(() => {
    const empty = new Map<ProcedureBlock, Record<number, StepImageBinding[]>>();
    if (!showRichMedia || !doc) return empty;

    const flat: Array<{
      block: ProcedureBlock;
      itemIdx: number;
      step: ParsedStep;
    }> = [];

    for (const block of displayBlocks) {
      if (block.kind !== "steps") continue;
      block.items.forEach((item, itemIdx) => {
        if (!item.num) return;
        flat.push({
          block,
          itemIdx,
          step: { num: item.num, text: item.text, marker: "dot" },
        });
      });
    }
    if (!flat.length) return empty;

    const bindings = buildStepImagesByIndex(
      flat.map((f) => f.step),
      inlineImages
    );
    const map = new Map<ProcedureBlock, Record<number, StepImageBinding[]>>();
    flat.forEach((f, flatIdx) => {
      let perBlock = map.get(f.block);
      if (!perBlock) {
        perBlock = {};
        map.set(f.block, perBlock);
      }
      perBlock[f.itemIdx] = bindings[flatIdx] ?? [];
    });
    return map;
  }, [showRichMedia, doc, displayBlocks, inlineImages]);

  const pageLabel = t("opva.chatAssistancePageShort");
  const zoomLabel = t("opva.chatAssistanceClickToEnlarge");

  const openStepImage = (
    bindings: StepImageBinding[],
    src: string,
    title?: string
  ) => {
    setLightbox({
      src,
      gallery: stepImagesToGallery(bindings, pageLabel),
      title,
    });
  };

  const onSectionCarouselScroll = useCallback(
    (sectionKey: string, el: HTMLDivElement) => {
      const max = el.scrollWidth - el.clientWidth;
      const next = max <= 0 ? 1 : Math.min(1, Math.max(0, el.scrollLeft / max));
      setScrollProgress((prev) =>
        prev[sectionKey] === next ? prev : { ...prev, [sectionKey]: next }
      );
    },
    []
  );

  if (!text.trim()) return null;

  if (!doc || displayBlocks.length === 0) {
    return <RagMarkdownContent text={cleanStepDisplayText(text)} />;
  }

  const renderStepCard = (
    item: ProcedureBlockItem,
    key: string,
    images: StepImageBinding[] = []
  ) => {
    const { title, body: rawBody } = splitStepTitleBody(item.text);
    const { text: bodyNoVideos, videos: stepVideos } = extractStepVideoReferences(
      rawBody,
      videos
    );
    const { text: body, link: actionLink } = extractStepActionLink(bodyNoVideos);
    return (
      <Box
        key={key}
        sx={{
          flex: "0 0 auto",
          width: { xs: 280, sm: 320, md: 360 },
          maxWidth: { xs: "85vw", md: "100%" },
          scrollSnapAlign: "start",
          alignSelf: "stretch",
          display: "flex",
        }}
      >
        <StepCard
          title={title}
          body={body}
          videos={stepVideos}
          actionLink={actionLink}
          images={images}
          onOpenImage={(src: string) =>
            openStepImage(
              images,
              src,
              [t("opva.chatAssistanceStepLabel", { num: item.num }), title]
                .filter(Boolean)
                .join(" · ")
            )
          }
          stepLabel={t("opva.chatAssistanceStepLabel", { num: item.num })}
          tapToOpenLabel={t("opva.chatAssistanceTapToOpen")}
          morePhotosLabel={(count: number) =>
            t("opva.chatAssistanceMorePhotos", { count })
          }
          zoomLabel={zoomLabel}
        />
      </Box>
    );
  };

  const renderStepsCarousel = (block: ProcedureBlock, sectionKey: string) => {
    const imagesByItem = wiStepImagesByBlock.get(block) ?? {};
    const stepItems = block.items.filter((it) => it.num);
    if (!stepItems.length) return null;
    const progress = scrollProgress[sectionKey] ?? 0;
    const progressWidth =
      stepItems.length <= 1
        ? 100
        : ((1 + progress * (stepItems.length - 1)) / stepItems.length) * 100;

    return (
      <Box key={sectionKey} sx={{ mt: 2.25 }}>
        {block.title ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <ProcedureSectionHeader title={block.title} />
            <Box component="span" sx={ANSWER_COUNT_CHIP_SX}>
              {stepItems.length}
            </Box>
          </Box>
        ) : null}
        <Box
          onScroll={(e) =>
            onSectionCarouselScroll(sectionKey, e.currentTarget)
          }
          sx={{
            display: "flex",
            gap: 1.5,
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            pb: 1,
            mt: 1,
          }}
        >
          {stepItems.map((item, i) =>
            renderStepCard(
              item,
              `${sectionKey}-${item.num}-${i}`,
              imagesByItem[i] ?? []
            )
          )}
        </Box>
        {stepItems.length > 1 ? (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                height: 3,
                borderRadius: 999,
                bgcolor: "grey.200",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${progressWidth}%`,
                  bgcolor: "primary.main",
                  borderRadius: 999,
                }}
              />
            </Box>
          </Box>
        ) : null}
      </Box>
    );
  };

  const openCompactImage = (
    bindings: StepImageBinding[],
    src: string,
    title?: string
  ) => {
    setLightbox({
      src,
      gallery: stepImagesToGallery(bindings, pageLabel),
      title,
    });
  };

  const renderBlock = (block: ProcedureBlock, index: number) => {
    if (block.kind === "compact") {
      return (
        <Box key={`post-compact-${index}`} sx={{ mt: 1.5 }}>
          <ProcedureCompactSection
            title={block.title}
            variant={block.compactVariant ?? "prose"}
            body={block.body}
            items={block.items}
            inlineImages={showRichMedia ? inlineImages : undefined}
            onOpenImage={(src, gallery) =>
              openCompactImage(gallery, src, block.title)
            }
            tapToOpenLabel={t("opva.chatAssistanceTapToOpen")}
            morePhotosLabel={(count: number) =>
              t("opva.chatAssistanceMorePhotos", { count })
            }
            zoomLabel={zoomLabel}
            imagePlacement={
              isBareRemovingOrInstallingTitle(block.title) ||
              isBareNoteSectionTitle(block.title)
                ? "beside"
                : "below"
            }
          />
        </Box>
      );
    }
    return renderStepsCarousel(block, `post-steps-${index}`);
  };

  const allBlocks = [...toolsBlocks, ...bodyBlocks];

  return (
    <Box sx={{ mt: 1.5 }}>
      {doc.documentTitle ? (
        <Typography
          component="h2"
          data-testid="catalog-post-document-title"
          sx={{ ...ANSWER_TITLE_SX, mb: 1.5 }}
        >
          {doc.documentTitle}
        </Typography>
      ) : null}
      {doc.intro ? (
        <Box sx={{ mb: 1.5, color: "text.secondary" }}>
          <RagMarkdownContent text={cleanStepDisplayText(doc.intro)} dense />
        </Box>
      ) : null}
      {allBlocks.map((block, i) => (
        <Fragment key={`post-block-${i}`}>{renderBlock(block, i)}</Fragment>
      ))}
      <RagImageLightbox
        src={lightbox?.src ?? null}
        gallery={lightbox?.gallery}
        title={lightbox?.title}
        onClose={() => setLightbox(null)}
      />
    </Box>
  );
}
